import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AlertLogListItemProps } from './alertLogListItem.typings'
import { useStyles } from './styles'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Avatar, Chip, ListItem, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import {
    createSnapshotFromUserAlert,
    getColorForAlertStatus,
    getUserAlertKey,
    getUserAlertMessage,
    getUserAlertUnderlyingSecurity
} from '../../../util/alerts'
import { formatUnixTimestamp } from '../../../util/dateTime'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { LOGOS_BASE_URL } from '../../../assets/constants'
import { getColorForSymbol } from '../../../util/general'
import { setAlertModalData } from '../../../reducer/alerts.reducer'
import { selectIndicators } from '../../../selectors/chart'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import DeleteIcon from '../../../assets/icons/DeleteIcon'
import { operatorDisplayNames } from '../alertOperatorDropdown/alertOperatorDropdown'
import { removeAlertLog as removeAlertLogApi } from '../../../services/apis/alerts'
import { removeAlertLog, removeFiredAlert } from '../../../reducer/alertLogs.reducer'
import { debounce } from 'throttle-debounce'
import { openChartInNewWindow } from '../../../util/common'
import { convertExpressionToDisplayExpression } from '../../../util/expression'
import { getTimeBucketDisplayName } from '../../../util/timeBuckets'
import BinIcon from '../../../assets/icons/BinIcon'

const AlertLogListItem: React.FC<AlertLogListItemProps> = ({ log, alertType }) => {
    const { classes, cx } = useStyles()
    const dispatch = useDispatch()

    const indicators = useSelector(selectIndicators)

    const { alert, triggeredTime, id } = log
    const key = useMemo(() => (alert ? getUserAlertKey(alert) : ''), [alert])
    const timeBucket = useMemo(() => alert?.timeBucket, [alert?.timeBucket])

    const [securityList = [], expression = ''] = useMemo(() => (alert ? getUserAlertUnderlyingSecurity(alert) : []), [])
    const isExpression = useMemo(() => !!expression.length, [expression])
    const theme = useTheme()
    const [primarySecurity, setPrimarySecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [expressionSecurities, setExpressionSecurities] = useState<Awaited<ReturnType<typeof getSecurityAsync>>[]>([])
    useEffect(() => {
        if (!securityList.length) {
            setPrimarySecurity(undefined)
            setExpressionSecurities([])
            return
        }
        if (!isExpression) {
            getSecurityAsync(getSecurityKey(securityList[0].exchange, securityList[0].code)).then(setPrimarySecurity)
            setExpressionSecurities([])
        } else {
            Promise.all(securityList.map((v) => getSecurityAsync(getSecurityKey(v.exchange, v.code)))).then(setExpressionSecurities)
            setPrimarySecurity(undefined)
        }
    }, [securityList, isExpression])

    const symbolsDisplayValue = useMemo(() => {
        if (!securityList.length) return ''
        let elements = ''
        if (!isExpression && primarySecurity) {
            elements += primarySecurity.displayName
        } else if (isExpression && expression.length && expressionSecurities.length) {
            elements += convertExpressionToDisplayExpression(expression, expressionSecurities)
        }

        if (!elements.length) return ''
        if (timeBucket) elements += `, ${getTimeBucketDisplayName(timeBucket)}`
        return elements
    }, [securityList, expression, isExpression, timeBucket, primarySecurity, expressionSecurities])

    const avatar = useMemo(() => {
        if (!securityList.length && !expression.length) return null
        const security = primarySecurity
        if (!security) return null
        let name = isExpression ? securityList.map((sec) => sec.code[0]).join('') : security.symbol || security.code
        if (isExpression || (!isExpression && !security.logoUrl)) {
            return (
                <Avatar alt={'Icon'}
                        color={getColorForSymbol(isExpression ? key : securityList[0]?.symbol || securityList[0]?.code || '', theme.palette.background.default)}
                        className={classes.avatar}>
                    {name
                        .split(' ')
                        .map((word) => word[0]?.toUpperCase())
                        .slice(0, 2)
                        .join('')}
                </Avatar>
            )
        }

        return <Avatar alt={'Icon'} src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`} className={classes.avatar} />
    }, [securityList, expression, key, isExpression, primarySecurity, theme.palette.background.default])

    const onClick = useCallback(() => {
        openChartInNewWindow(key)
    }, [key])

    const onDoubleClick = useCallback(() => {
        if (!alert) return
        const chartSnapshot = createSnapshotFromUserAlert(alert, indicators)
        dispatch(
            setAlertModalData({
                chartSnapshot,
                edit: true,
                alert
            })
        )
    }, [alert, indicators, alertType])

    const onLogRemoveClick = useCallback(
        debounce(100, async () => {
            try {
                await removeAlertLogApi({
                    id
                })
                dispatch(removeAlertLog(id))
            } catch (error) {
            }
        }),
        [id]
    )

    const onFiredAlertRemoveClick = useCallback(() => {
        dispatch(removeFiredAlert(id))
    }, [id])

    const displayName = useMemo(() => {
        if (!securityList.length) return ''
        if (expression.length) return expression
        return primarySecurity?.symbol || primarySecurity?.code || ''
    }, [securityList, expression, primarySecurity])

    const handleOnIconsClick = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation()
    }, [])

    const tooltipContent = useMemo(() => {
        if (!alert) return ''
        const operator = alert.condition?.securityAlertCondition?.operator
        const messageFromAlert = alert.condition?.securityAlertCondition ? getUserAlertMessage(alert.condition.securityAlertCondition, symbolsDisplayValue, indicators) : ''
        const operatorPosition = messageFromAlert.indexOf(operatorDisplayNames[operator!])

        return (
            <Stack className={classes.tooltip}>
                {alert.name && <Typography fontSize={'small'}>{alert.name}</Typography>}
                {alert.message && messageFromAlert !== alert.message &&
                    <Typography fontSize={'small'}>{alert.message}</Typography>}
                {alert.condition?.securityAlertCondition && (
                    <Stack direction="row" gap={0.5} className={classes.fullWidth}>
                        <Typography fontSize={'small'} component={'span'} className={classes.wrapOverflow}>
                            {messageFromAlert.substring(0, operatorPosition)}
                            <Typography component={'span'} color={'warning.light'} fontSize={'small'}>
                                {operatorDisplayNames[operator!]}
                            </Typography>
                            {messageFromAlert.substring(operatorPosition + operatorDisplayNames[operator!].length)}
                        </Typography>
                    </Stack>
                )}
                <Stack direction="row" gap={0.5} className={classes.fullWidth}>
                    <Typography variant={'caption'} className={classes.wrapOverflow}>
                        <Typography component={'span'}
                                    color={getColorForAlertStatus(alert.secondaryStatus, theme.palette.mode === 'light')}
                                    variant="caption">
                            {alert.secondaryStatus}
                        </Typography>
                        {`${'\u00B7'} ${key}`}
                    </Typography>
                </Stack>
                <Typography variant={'caption'}
                            className={cx(classes.fullWidth, classes.wrapOverflow, classes.tooltipCaption)}>{`Created: ${formatUnixTimestamp(alert.created)}`}</Typography>
                <Typography
                    variant={'caption'}
                    className={cx(classes.fullWidth, classes.wrapOverflow, classes.tooltipCaption)}
                >{`Last triggered: ${formatUnixTimestamp(alert.lastTriggered)}`}</Typography>
            </Stack>
        )
    }, [alert, key, displayName, indicators])

    const handleEditAlertClick = useCallback(() => {
        if (!alert) return
        onDoubleClick()
        if (alertType === 'fired') onFiredAlertRemoveClick()
    }, [onDoubleClick, onFiredAlertRemoveClick, alertType, alert])

    const alertButtons = useMemo(() => {
        return (
            <Stack direction={'row'} gap={0.5}
                   className={cx(classes.displayOnHover, classes.minW0, classes.cursorPointer)}>
                {alertType === 'log' && (
                    <NonInteractiveTooltip title={'Delete'} arrow placement={'top'}>
                        <ExtraSmallButton
                            onClick={onLogRemoveClick}
                            color="inherit"
                            aria-label="delete"
                            data-event={'Alert Deleted'}
                            data-event-alert-name={alert?.name}
                            data-event-alert-message={alert?.message}
                            className={classes.hoveredButton}
                        >
                            <DeleteIcon className={classes.hoveredIcon} />
                        </ExtraSmallButton>
                    </NonInteractiveTooltip>
                )}
                {alertType === 'fired' && (
                    <>
                        <ExtraSmallButton
                            onClick={handleEditAlertClick}
                            color="inherit"
                            aria-label="Edit"
                            data-event={'Fired Alert Edited'}
                            data-event-alert-name={alert?.name}
                            data-event-alert-message={alert?.message}
                            className={classes.hoveredButton}
                        >
                            <EditOutlinedIcon className={classes.hoveredIcon} />
                        </ExtraSmallButton>
                        <ExtraSmallButton
                            onClick={onFiredAlertRemoveClick}
                            color="inherit"
                            aria-label="remove"
                            data-event={'Fired Alert Removed'}
                            data-event-alert-name={alert?.name}
                            data-event-alert-message={alert?.message}
                            className={classes.hoveredButton}
                        >
                            <BinIcon className={classes.hoveredIcon} />
                        </ExtraSmallButton>
                    </>
                )}
            </Stack>
        )
    }, [onLogRemoveClick, alert?.name, alert?.message, classes, alertType, onFiredAlertRemoveClick])

    const hoverButtons = useMemo(() => {
        return (
            <Stack direction={'row'} className={classes.hoveredButtons} onClick={handleOnIconsClick}>
                <OpenInNewIcon onClick={onClick}
                               className={cx(classes.iconHover, classes.displayOnHover, classes.verticalAlignMiddle, classes.fullHeight, classes.cursorPointer)} />
                {alertButtons}
            </Stack>
        )
    }, [alertButtons, classes])

    const nameAndMessage = useMemo(() => {
        return (
            <Stack className={classes.fullWidth}>
                {alert?.name && (
                    <Stack className={cx(classes.fullWidth, classes.alignCenter, classes.h22)} direction={'row'}>
                        <Typography fontWeight={500}
                                    className={cx('font-feature-num', classes.alertName, classes.minW0)}>
                            {alert.name}
                        </Typography>
                        {hoverButtons}
                    </Stack>
                )}
                {alert?.message && (
                    <Stack className={cx(classes.fullWidth, classes.alignCenter, classes.h22)} direction={'row'}>
                        <Typography
                            className={cx('font-feature-num', classes.alertMessage, classes.minW0)}>{alert.message}</Typography>
                        {!alert?.name && hoverButtons}
                    </Stack>
                )}
            </Stack>
        )
    }, [alert?.name, alert?.message, classes, onClick, hoverButtons])

    const attributes = useMemo(() => {
        return (
            <Stack gap={0.5} direction="row" className={alertType === 'fired' ? classes.attributesContainer : ''}>
                {alertType === 'fired' &&
                    <Chip label={displayName} icon={avatar ?? undefined} size="small" onClick={onClick}
                          className={classes.attributesChip} />}
                {alertType === 'log' && avatar}
                {alertType === 'log' && (
                    <Typography variant={'caption'} color={''}>
                        {displayName}
                    </Typography>
                )}
                {alertType === 'log' && triggeredTime && <Typography variant={'caption'}>{'\u00B7'}</Typography>}
                {triggeredTime && (
                    <Typography variant={'caption'} color={'text.secondary'}>
                        {formatUnixTimestamp(triggeredTime, alertType === 'fired')}
                    </Typography>
                )}
            </Stack>
        )
    }, [avatar, displayName, triggeredTime, alertType, onClick])

    const editAlert = useMemo(() => {
        return (
            <Stack direction={'row'}>
                <ExtraSmallButton variant="text" onClick={handleEditAlertClick} color={'primary'} size={'small'}>
                    {'Edit alert'}
                </ExtraSmallButton>
            </Stack>
        )
    }, [handleEditAlertClick])

    const body = useMemo(() => {
        return (
            <ListItem
                key={id}
                className={cx(alertType === 'log' ? classes.parentContainer : classes.firedContainer)}
                onDoubleClick={alertType === 'log' ? onDoubleClick : undefined}
                sx={{
                    color: undefined
                }}
            >
                {nameAndMessage}
                {attributes}
            </ListItem>
        )
    }, [alertType, editAlert, alertButtons, nameAndMessage, attributes, id, classes.parentContainer, classes.primaryBordered, onClick, onDoubleClick])

    return useMemo(() => {
        if (alertType === 'log') {
            return (
                <Tooltip title={tooltipContent} arrow={true} placement="left">
                    {body}
                </Tooltip>
            )
        } else {
            return body
        }
    }, [alertType, body, tooltipContent])
}
export default memo(AlertLogListItem)
